import React, { useState, useCallback, useRef, useEffect } from "react";
import { Box, Typography, Switch, Skeleton, Stack } from "@mui/material";
import { FormProvider } from "react-hook-form";
import PageHeader from "@ui/components/header/PageHeader";
import { PageContainer, PageContent } from "@ui/components/wraper";
import { FInput } from "@ui/components/inputs/FInput";
import { useRoleData } from "../hooks/useRoleData";
import { useRoleForm } from "../hooks/useRoleForm";
import { usePermissionsGrid } from "../hooks/usePermissionsGrid";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@ui/hooks/usePermissions";
import { Permission } from "@utility/permission-checker";
import { SecondaryGrid } from "@ui/components/grid/SecondaryGrid";
import { PermissionCard } from "./components/PermissionCard";
import { EditPermissionFollowUpDialog, } from "./components/EditPermissionFollowUpDialog";
const AddEditRolePage = () => {
    var _a, _b, _c;
    const { t } = useTranslation("common");
    const { hasPermission } = usePermissions();
    // Use custom hooks for data and form management
    const { isEdit, role, roleExists, loading: roleLoading } = useRoleData();
    const { methods, control, errors, handleSubmit, validationRules, handleFormSubmit, handlePermissionChange, setModuleExtendedPermission, loading, } = useRoleForm({ isEdit, role });
    const [followUpKind, setFollowUpKind] = useState(null);
    const followUpHoverTimerRef = useRef(null);
    const clearFollowUpHoverTimer = useCallback(() => {
        if (followUpHoverTimerRef.current != null) {
            clearTimeout(followUpHoverTimerRef.current);
            followUpHoverTimerRef.current = null;
        }
    }, []);
    useEffect(() => () => clearFollowUpHoverTimer(), [clearFollowUpHoverTimer]);
    const scheduleFollowUpDialogOnHover = useCallback((moduleId, editOn) => {
        clearFollowUpHoverTimer();
        if (!editOn)
            return;
        if (moduleId !== "AccessPoints" && moduleId !== "Nodes")
            return;
        followUpHoverTimerRef.current = window.setTimeout(() => {
            followUpHoverTimerRef.current = null;
            if (moduleId === "AccessPoints")
                setFollowUpKind("ap-server");
            else
                setFollowUpKind("node-hardware");
        }, 700);
    }, [clearFollowUpHoverTimer]);
    const roleClaims = useWatch({
        control,
        name: "roleClaims",
    }) || [];
    const handlePermissionChangeWrapped = useCallback((moduleName, permissionType, checked) => {
        var _a, _b;
        const claims = methods.getValues("roleClaims") || [];
        const wasEdit = (_b = (_a = claims.find((c) => c.module === moduleName)) === null || _a === void 0 ? void 0 : _a.editPermission) !== null && _b !== void 0 ? _b : false;
        if (permissionType === "editPermission" && !checked) {
            if (moduleName === "AccessPoints" || moduleName === "Nodes") {
                setFollowUpKind(null);
                clearFollowUpHoverTimer();
            }
        }
        handlePermissionChange(moduleName, permissionType, checked);
        if (permissionType !== "editPermission" || !checked || wasEdit)
            return;
        if (moduleName === "AccessPoints")
            setFollowUpKind("ap-server");
        else if (moduleName === "Nodes")
            setFollowUpKind("node-hardware");
    }, [handlePermissionChange, methods, clearFollowUpHoverTimer]);
    const followUpExtendedValue = followUpKind === "ap-server"
        ? !!((_a = roleClaims.find((c) => c.module === "AccessPoints")) === null || _a === void 0 ? void 0 : _a.serverSettingsPermission)
        : followUpKind === "node-hardware"
            ? !!((_b = roleClaims.find((c) => c.module === "Nodes")) === null || _b === void 0 ? void 0 : _b.nodeHardwareSettingsPermission)
            : false;
    const handleFollowUpConfirm = (allow) => {
        if (followUpKind === "ap-server") {
            setModuleExtendedPermission("AccessPoints", "serverSettingsPermission", allow);
        }
        else if (followUpKind === "node-hardware") {
            setModuleExtendedPermission("Nodes", "nodeHardwareSettingsPermission", allow);
        }
    };
    const { gridRows } = usePermissionsGrid({
        roleClaims,
    });
    const columns = [
        {
            field: "module",
            headerName: t("grid.module"),
            flex: 2,
            minWidth: 150,
            filterable: false,
        },
        {
            field: "viewPermission",
            headerName: t("grid.view"),
            flex: 1,
            minWidth: 100,
            filterable: false,
            renderCell: (params) => {
                if (!params.row.hasView)
                    return React.createElement(Typography, null, "-");
                return (React.createElement(Switch, { checked: params.row.viewPermission, onChange: (e) => handlePermissionChangeWrapped(String(params.row.id), "viewPermission", e.target.checked), disabled: params.row.isDashboard || params.row.isAccessPoints, size: "small" }));
            },
        },
        {
            field: "createPermission",
            headerName: t("grid.create"),
            flex: 1,
            minWidth: 100,
            filterable: false,
            renderCell: (params) => {
                if (!params.row.hasCreate)
                    return React.createElement(Typography, null, "-");
                return (React.createElement(Switch, { checked: params.row.createPermission, onChange: (e) => handlePermissionChangeWrapped(String(params.row.id), "createPermission", e.target.checked), size: "small" }));
            },
        },
        {
            field: "editPermission",
            headerName: t("grid.edit"),
            flex: 1,
            minWidth: 100,
            filterable: false,
            renderCell: (params) => {
                if (!params.row.hasEdit)
                    return React.createElement(Typography, null, "-");
                const rowId = String(params.row.id);
                const isExtendedFollowUpRow = rowId === "AccessPoints" || rowId === "Nodes";
                const switchEl = (React.createElement(Switch, { checked: params.row.editPermission, onChange: (e) => handlePermissionChangeWrapped(rowId, "editPermission", e.target.checked), size: "small" }));
                if (!isExtendedFollowUpRow)
                    return switchEl;
                return (React.createElement(Box, { onMouseEnter: () => scheduleFollowUpDialogOnHover(rowId, !!params.row.editPermission), onMouseLeave: clearFollowUpHoverTimer, sx: { display: "inline-flex", alignItems: "center" } }, switchEl));
            },
        },
        {
            field: "deletePermission",
            headerName: t("grid.delete"),
            flex: 1,
            minWidth: 100,
            filterable: false,
            renderCell: (params) => {
                if (!params.row.hasDelete)
                    return React.createElement(Typography, null, "-");
                return (React.createElement(Switch, { checked: params.row.deletePermission, onChange: (e) => handlePermissionChangeWrapped(String(params.row.id), "deletePermission", e.target.checked), size: "small" }));
            },
        },
    ];
    // Show loading skeleton while fetching role
    if (isEdit && roleLoading) {
        return (React.createElement(PageContainer, null,
            React.createElement(Box, { sx: { p: 3 } },
                React.createElement(Stack, { direction: "row", alignItems: "center", spacing: 2, sx: { mb: 3 } },
                    React.createElement(Skeleton, { variant: "circular", width: 40, height: 40 }),
                    React.createElement(Skeleton, { variant: "rectangular", width: 200, height: 32 }))),
            React.createElement(PageContent, null,
                React.createElement(Stack, { spacing: 3 },
                    React.createElement(Skeleton, { variant: "rectangular", height: 56 }),
                    React.createElement(Stack, { spacing: 1 }, [...Array(8)].map((_, index) => (React.createElement(Skeleton, { key: index, variant: "rectangular", height: 52 }))))))));
    }
    // Early return for role not found
    if (isEdit && !roleExists) {
        return (React.createElement(PageContainer, null,
            React.createElement(PageHeader, { title: t("pages.roleNotFound"), showBackButton: true })));
    }
    // Action button configuration - only show if user has appropriate permission
    const canSubmit = isEdit
        ? hasPermission(Permission.ROLES_EDIT)
        : hasPermission(Permission.ROLES_CREATE);
    const actionButton = canSubmit
        ? {
            label: isEdit ? t("actions.updateRole") : t("actions.addRole"),
            onClick: handleSubmit(handleFormSubmit),
            variant: "contained",
            size: "large",
            loading: loading,
        }
        : undefined;
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: isEdit ? `${t("pages.editRole")}: ${role === null || role === void 0 ? void 0 : role.name}` : t("pages.addRole"), showBackButton: true, bottomActionButton: actionButton }),
        React.createElement(PageContent, { hasBottomAction: true },
            React.createElement(FormProvider, Object.assign({}, methods),
                React.createElement("form", { onSubmit: handleSubmit(handleFormSubmit) },
                    React.createElement(FInput, { name: "name", control: control, label: t("labels.roleName"), placeholder: t("placeholders.admin"), error: errors.name, helperText: (_c = errors.name) === null || _c === void 0 ? void 0 : _c.message, rules: validationRules.name, variant: "filled", fullWidth: true }),
                    React.createElement(SecondaryGrid, { rows: gridRows, columns: columns, loading: loading, hideFooter: true, fillRemainingSpace: false, gridMaxHeight: "calc(100dvh - 200px)", checkboxSelection: false, renderCard: (row) => (React.createElement(PermissionCard, { module: row, onPermissionChange: handlePermissionChangeWrapped, onExtendedSettingsHoverEnter: scheduleFollowUpDialogOnHover, onExtendedSettingsHoverLeave: clearFollowUpHoverTimer })) }))),
            React.createElement(EditPermissionFollowUpDialog, { open: followUpKind !== null, kind: followUpKind, extendedValue: followUpExtendedValue, onClose: () => setFollowUpKind(null), onConfirm: handleFollowUpConfirm }))));
};
export default AddEditRolePage;
